import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_RANGE, EPIC_SLUGS } from "../../constants/epicLeaderboard";
import type { RangeOption } from "../../constants/epicLeaderboard";
import type { PlayerEpicStat } from "../../types/epicLeaderboard";
import { getEpicColor } from "../../utils/epicColors";
import BossChips from "./BossChips";
import EpicRunNote from "./EpicRunNote";
import { formatDate } from "./format";
import PlayerEpicTimesChart from "./PlayerEpicTimesChart";
import "./epicLeaderboard.css";

// A line chart needs at least two points to say anything.
const MIN_CHART_RUNS = 2;
// Mirrors the API: average rank only exists from this many runs.
const MIN_AVERAGE_RANK_RUNS = 3;

const slugForEpic = (epicName: string): string | undefined =>
  Object.keys(EPIC_SLUGS).find((slug) => EPIC_SLUGS[slug] === epicName);

// Deep link to this epic's leaderboard, pre-filtered to the player.
const leaderboardPath = (
  epicName: string,
  player: string,
  range: RangeOption
): string | null => {
  const slug = slugForEpic(epicName);
  if (!slug) return null;
  const params = new URLSearchParams({ epic: slug, player });
  if (range !== DEFAULT_RANGE) params.set("range", range);
  return `/epic-leaderboard?${params.toString()}`;
};

const Stat = ({
  label,
  value,
  children
}: {
  label: string;
  value: ReactNode;
  children?: ReactNode;
}) => (
  <div>
    <div className="epic-lb-card-title mb-1">{label}</div>
    <div className="player-epic-stat-value">{value}</div>
    {children}
  </div>
);

type Props = {
  stat: PlayerEpicStat;
  player: string;
  range: RangeOption;
};

const PlayerEpicCard = ({ stat, player, range }: Props) => {
  const color = getEpicColor(stat.epic);
  const path = leaderboardPath(stat.epic, player, range);

  // The whole card links to the leaderboard (a stretched link on the title),
  // so the note toggle and chart sit above it as separate interactive bits
  // instead of nesting inside an anchor.
  return (
    <div
      className={`epic-lb-card epic-lb-first-record${
        path ? " player-epic-card" : ""
      }`}
      style={{ borderLeftColor: color }}
    >
      <div className="epic-lb-card-title">
        {path ? (
          <Link to={path} className="player-epic-card-link">
            {stat.epic}
          </Link>
        ) : (
          stat.epic
        )}
      </div>
      <div className="player-epic-stats">
        <Stat label="Fastest" value={stat.fastest}>
          <div className="epic-lb-muted">
            Rank #{stat.fastestRank} of {stat.rankedPlayers}
          </div>
          <div className="epic-lb-muted">{formatDate(stat.fastestRunDate)}</div>
          {stat.fastestTeammates.length > 0 && (
            <div className="epic-lb-muted player-epic-teammates">
              with {stat.fastestTeammates.join(", ")}
            </div>
          )}
        </Stat>
        <Stat label="Average" value={stat.average}>
          <div className="epic-lb-muted">
            {stat.averageRank === null
              ? `Rank needs ${MIN_AVERAGE_RANK_RUNS} runs`
              : `Rank #${stat.averageRank}`}
          </div>
        </Stat>
        <Stat label="Runs" value={stat.runCount} />
        <Stat label="Last run" value={formatDate(stat.lastRunDate)} />
      </div>
      <div className="player-epic-run-extras">
        <BossChips bosses={stat.fastestRunBosses} />
        <EpicRunNote runId={stat.fastestRunId} note={stat.fastestRunNote} />
      </div>
      {stat.runs.length >= MIN_CHART_RUNS && (
        <div className="mt-3 player-epic-chart">
          <PlayerEpicTimesChart epicName={stat.epic} runs={stat.runs} />
        </div>
      )}
    </div>
  );
};

export default PlayerEpicCard;
